import { apiCall } from 'app/shared/lib/essette-api';
import { ComponentBase } from 'app/shared/lib/essette-site';
import { autoinject, bindable } from 'aurelia-framework';
import { MemberService } from 'app/cd/services/member-service';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import * as CD from 'app/cd/models/essette-api';



@autoinject()
export class MemberMergeHistory extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable()
    protected memberId: string | null = null;

    protected mergeResults: CD.MemberMergeResult[] = [];
    protected externalIdMap: Map<string, string | null> = new Map<string, string>();
    protected resultCount: number = 0;
    protected pageSize: number = 10;


    ////****************** Element References ***************************************////
    protected $pager!: EssPagerCustomElement;


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly memberService: MemberService,
        ...rest: any[]) {

        super(...rest);
    }

    protected async bind(): Promise<void> {
        await this.reload();
        // NOTE [JP] Currently this redirect is here because there is the possibility for a user to navigate directly to
        // the Merge History tab (via URL) even if the member has no merge history data. Since this is not a permissions issue, and is
        // merely a display issue, this is here to ensure that the tab is not displayed if it is not meant to be.
        if(this.mergeResults.length === 0) {
            const routeIndex = this.router.routes.findIndex((route) => route.route === 'merge-history');
            if(routeIndex >= 0) {
                this.router.routes[routeIndex].settings.isVisible = false;
                this.router.navigateToRoute('core.admin.member.info');
            }
        }
    }


    ////****************** Functions ************************************************////
    public async reload(): Promise<void> {
        if(!this.memberId) return;
        await this.loadMemberMergeResultDetails();
        await this.loadMemberExternalIds();
    }

    protected getExternalId(memberId: string): string | null {
        return this.externalIdMap.get(memberId) || null;
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    public async loadMemberMergeResultDetails(): Promise<void> {
        if(!this.memberId) return;

        const options: Partial<IQueryLoadOptions> = {
            top: this.pageSize,
            page: this.$pager?.page || 0,
        };

        const includes: string[] = [
            'details',
            'details.related',
            'target',
            'source',
            'dateRange',
            'byUser'
        ];

        const fields: string[] = [
            'details.related.label',
            'details.related.recordCount',
            'target.mergeDisplay',
            'source.mergeDisplay',
            'dateRange.end',
            'byUser.$display'
        ];

        try {
            this.state.beginLoad();
            const response = await this.memberService.loadMemberMergeResults(this.memberId, options, includes, fields);
            this.mergeResults = response.results;
            this.resultCount = response.count || 0;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    @apiCall
    public async loadMemberExternalIds(): Promise<void> {
        if(this.mergeResults.length === 0) return;
        // isolate Member IDs present in Merge History (from Source and Target Members)
        const memberIds: Set<string> = new Set<string>();
        this.mergeResults.forEach((result) => {
            if(!!result.target?.id) memberIds.add(result.target.id);
            if(!!result.source?.id) memberIds.add(result.source.id);
        });

        const options: Partial<IQueryLoadOptions> = {
            includeInactive: true
        };

        const includes: string[] = [
            'identifier'
        ];

        const fields: string[] = [
            'identifier.external'
        ];

        try {
            this.state.beginLoad();
            const response = await this.memberService.loadMembersByIds([...memberIds], options, includes, fields);
            // Map Member IDs to their associated external ID
            response.results.forEach((member: CD.Member) => {
                if(!!member.id) this.externalIdMap.set(member.id, member.identifier?.external || null)
            });
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }
}
